import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Brand, cn, ThemeToggleButton } from '@/core/ui';
import { ArrowLeft } from '@/core/ui/icons';
import { detectInAppBrowser } from '../domain/in-app-browser';
import { LoginForm } from './LoginForm';
import { SignupForm, type ExistingAccountHandoff } from './SignupForm';
import { OrSeparator, SocialLogin } from './SocialLogin';

export type AuthMode = 'login' | 'signup';

interface Props {
  /** Entrar ou criar conta — a rota guarda na URL (`?cadastro=true`). */
  mode?: AuthMode;
  onModeChange?: (mode: AuthMode) => void;
  /** Para onde a pessoa vai depois de entrar — muda a frase de abertura. */
  redirect?: string;
}

/**
 * Tela de entrada e de cadastro. Usuário e senha são validados pelo Keycloak
 * (grant `password`); a tela é nossa, a autenticação continua sendo dele. O
 * "Criar conta" passa pela API, que cria a conta, e entra em seguida. O
 * redirect pós-login é feito pela rota (routes/login.tsx) — o mesmo nos dois
 * casos.
 *
 * Com o Google/Apple, a página sai da SPA (redirect pelo Keycloak) e volta
 * por /auth/callback para a mesma página de origem. Dentro de um app
 * (Instagram, WhatsApp…), onde o Google recusa o login, o e-mail vem primeiro
 * e os botões sociais explicam como abrir no navegador.
 *
 * O bloco fica no alto da página, não centralizado: no celular o teclado
 * cobre a metade de baixo, e é lá que o formulário ficava.
 *
 * Duas metades: à esquerda, a marca (só em telas largas); à direita, o
 * formulário, limpo, sobre o fundo da página.
 */
export function LoginPage({ mode = 'login', onModeChange, redirect }: Props) {
  const toLesson = !!redirect?.startsWith('/cursos/');
  const signup = mode === 'signup';
  const [inApp] = useState(() =>
    detectInAppBrowser(typeof navigator === 'undefined' ? '' : navigator.userAgent),
  );
  // E-mail que já tem conta (ou login automático que falhou): o login abre
  // com ele preenchido. A `key` remonta o formulário com os valores novos.
  const [handoff, setHandoff] = useState<ExistingAccountHandoff | null>(null);

  const switchTo = (next: AuthMode) => onModeChange?.(next);
  const toLogin = (h: ExistingAccountHandoff) => {
    setHandoff(h);
    switchTo('login');
  };

  const title = signup
    ? toLesson
      ? 'Crie sua conta para assistir'
      : 'Crie sua conta'
    : toLesson
      ? 'Entre para assistir'
      : 'Bem-vindo de volta';
  const subtitle = signup
    ? 'Leva menos de um minuto. Depois, você já entra e continua de onde estava.'
    : toLesson
      ? 'Depois de entrar, a aula começa de onde você estava.'
      : 'Entre com seu e-mail e senha para continuar.';

  const form = signup ? (
    <SignupForm onExistingAccount={toLogin} />
  ) : (
    <LoginForm
      key={handoff ? `${handoff.email}|${handoff.forgot}` : 'login'}
      returnTo={redirect}
      defaultUsername={handoff?.email}
      defaultForgotOpen={handoff?.forgot}
    />
  );
  const social = <SocialLogin returnTo={redirect} inApp={inApp} />;

  return (
    <div className="grid min-h-screen lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      <Showcase redirect={redirect} />

      <div className="relative flex flex-col px-5 pt-3 pb-6 sm:px-10 sm:pt-6">
        <div className="flex items-center justify-between">
          {/* No celular a metade da marca some: o voltar fica aqui. */}
          <BackButton redirect={redirect} className="lg:invisible" />
          <ThemeToggleButton className="rounded-full bg-transparent hover:bg-surface-2" />
        </div>

        <main className="mx-auto flex w-full max-w-[380px] flex-col pt-3 pb-10 sm:pt-8 lg:pt-[min(12vh,7rem)]">
          <Brand className="mb-6 lg:hidden" />
          <h1 className="text-[1.7rem] leading-tight font-semibold tracking-tight sm:text-[2rem]">{title}</h1>
          <p className="mt-2 text-base text-fg-subtle">{subtitle}</p>

          <div className="mt-7 space-y-5 sm:mt-9">
            {inApp ? (
              <>
                {form}
                <OrSeparator />
                {social}
              </>
            ) : (
              <>
                {social}
                <OrSeparator />
                {form}
              </>
            )}
          </div>

          <p className="mt-8 text-center text-sm text-fg-subtle">
            {!signup && 'Ainda não tem conta? '}
            <button
              type="button"
              onClick={() => switchTo(signup ? 'login' : 'signup')}
              className="font-medium text-brand-300 transition-colors hover:text-brand-400"
            >
              {signup ? 'Já tenho conta' : 'Criar conta'}
            </button>
          </p>
        </main>
      </div>
    </div>
  );
}

/**
 * Metade da marca: um fundo escuro com um leve brilho na cor da marca, o
 * logo e a chamada. Cores fixas — a metade é escura nos dois temas. Fica
 * presa na tela: o "Criar conta" é mais alto que ela, e a chamada sumia
 * lá embaixo.
 */
function Showcase({ redirect }: { redirect?: string }) {
  return (
    <aside className="relative isolate hidden overflow-hidden bg-[oklch(0.17_0.02_200)] p-12 text-white lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col xl:p-16">
      <div
        aria-hidden
        className="absolute -bottom-40 -left-40 -z-10 size-[36rem] rounded-full bg-[oklch(0.7_0.12_185/0.18)] blur-[140px]"
      />
      <BackButton redirect={redirect} onDark />
      <Brand className="mt-auto text-white" />
      <h2 className="mt-8 max-w-lg text-4xl leading-[1.1] font-bold tracking-tight text-balance xl:text-5xl">
        Aprenda no seu ritmo. <span className="text-[oklch(0.88_0.12_184)]">Evolua na prática.</span>
      </h2>
    </aside>
  );
}

/**
 * Volta para onde a pessoa estava: a aula que ela tentou assistir (na
 * página aberta do curso) ou, sem origem conhecida, a vitrine de aulas.
 */
function BackButton({
  redirect,
  onDark = false,
  className,
}: {
  redirect?: string;
  onDark?: boolean;
  className?: string;
}) {
  const course = /^\/cursos\/([^/?#]+)(?:\/assistir)?(?:\?aula=([^&#]+))?/.exec(redirect ?? '');
  const style = cn(
    'group inline-flex h-10 w-fit items-center gap-2 rounded-full pr-4 pl-3 text-sm font-medium transition-colors',
    onDark
      ? 'text-white/80 hover:bg-white/10 hover:text-white'
      : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
    className,
  );
  const content = (
    <>
      <ArrowLeft aria-hidden className="size-4 transition-transform group-hover:-translate-x-0.5" />
      Voltar
    </>
  );
  return course && course[1] !== 'novo' ? (
    <Link to="/cursos/$slug" params={{ slug: course[1]! }} search={{ aula: course[2] }} className={style}>
      {content}
    </Link>
  ) : (
    <Link to="/cursos" className={style}>
      {content}
    </Link>
  );
}
